import React from 'react';
import { connect } from 'react-redux';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

import userFormStyle from '../assets/jss/crm/views/userFormStyle';

import { addFirm, updateFirm } from '../store/slices/firmsSlice';
import { addPerson } from '../store/slices/personsSlice';
import { addVisibleFirm, addFirmContact } from '../store/slices/firmInfoSlice';
import { addVisibleFirmWithContacts } from '../store/slices/orgContactsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  validatePhoneNumber,
  validateEmail,
  validateAddress,
  constructPhoneRolesString,
  sortedDropdownItemLabels,
  showAlert,
} from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import KSelect from '../components/KSelect';

import Modal from './Modal';
import BusinessTypeWidget from './BusinessTypeWidget';
import PhoneInput from './PhoneInput';
import StateSelector from './StateSelector';
import WebLinkEditor from './WebLinkEditor';
import CrmTextField from './CrmTextField';
import AlertModal from './AlertModal';

const newReferrerOrgModalStyle = (theme) => ({
  ...userFormStyle,
  newReferrerOrgModal: {
    maxWidth: '700px',
    [theme.breakpoints.down('md')]: {
      top: 260,
    },
  },
  radioButton: {
    padding: '8px 3px 8px 12px',
    '&>span>svg': {
      fontSize: '18px',
    },
  },
  businessTypeLabel: {
    marginBottom: '-4px',
    fontSize: '15px',
  },
  radioDiv: {
    height: '30px',
    cursor: 'pointer',
  },
  textFieldHolder: {
    marginTop: '20px',
  },
  contactSectionLabelForPractitioner: {
    marginTop: '90px',
  },
  contactSectionLabel: {
    margin: '100px 0 -10px 20px',
  },
  contactSectionFields: {
    marginLeft: '18px',
  },
  phone2Div: {
    marginTop: '12px',
  },
  emailAddressDivForPractitioner: {
    marginTop: '39px',
  },
  descriptionLabel: {
    color: 'rgba(0,0,0,0.32)',
  },
  stateAndZipDiv: {
    display: 'flex',
  },
  stateDiv: {
    marginTop: '20px',
    flexBasis: '60%',
  },
  zipDiv: {
    marginLeft: '24px',
    marginTop: '12px',
    flexBasis: '40%',
  },
});

class NewReferrerOrgModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: undefined,
      firmType: 'Treatment Center',
      companyName: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      companyDescription: '',
      companyTemperature: 'Unknown',
      contactName: '',
      contactJobTitle: '',
      contactDescription: '',
      contactPhone1Roles: '',
      contactPhone1Number: '',
      contactPhone2Roles: '',
      contactPhone2Number: '',
      contactEmail: '',
      contactTemperature: 'Unknown',
      contactOwnerId: this.props.userInfo.personId,
      webUrl: '',
    };
  }

  cleanState = {
    errorMessage: undefined,
    firmType: 'Treatment Center',
    companyName: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    companyDescription: '',
    companyTemperature: 'Unknown',
    contactName: '',
    contactJobTitle: '',
    contactDescription: '',
    contactPhone1Roles: '',
    contactPhone1Number: '',
    contactPhone2Roles: '',
    contactPhone2Number: '',
    contactEmail: '',
    contactTemperature: 'Unknown',
    contactOwnerId: this.props.userInfo.personId,
    webUrl: '',
  };

  validationPolicy = {
    requireFirm: true,
    requireFirmType: true,
    requireAddress: false,
    forbidPartialAddress: true,
    allowZipFreeAddress: true,
    requireContact: true,
    requireAtLeaseOneOfContactPhoneOrEmail: true,
    requireJobTitle: false,
    requireContactDescription: false,
    requireContactPhone: false,
    requireContactEmail: false,
  };

  possibleCompanyTemperatures = sortedDropdownItemLabels('Referral Company Temperature');
  possibleContactTemperatures = sortedDropdownItemLabels('Referral Contact Temperature');

  componentDidMount() {
    if (this.props.fixedOrgType) {
      this.setState({ firmType: this.props.fixedOrgType });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.viewingFirmId !== prevProps.viewingFirmId || this.props.fixedOrgType !== prevProps.fixedOrgType) {
      this.setState({ firmType: this.props.fixedOrgType });
    }
  }

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleContactPhoneRoles = ['Work', 'Mobile', 'Home'];

  handleTextChange(event) {
    const rawValue = event.target.value;
    const value = event.target.id === 'contactEmail' ? rawValue.trim() : rawValue;
    this.setState({ [event.target.id]: value });
  }

  handleCompanyStateChange(newValue) {
    this.setState({ state: newValue });
  }

  validateAndSubmit() {
    const validation = this.validateForm();
    if (!validation.valid) {
      showAlert(this, validation.errorMessage);
      return;
    }
    this.submit();
  }

  submit() {
    const uri = '/api/firm';
    const firmApiStruct = this.buildFirmApiStruct();
    const request = createStdRequest(uri, 'POST', firmApiStruct);
    const actionOnGoodResponse = (json) => {
      const createFirmResponse = json;
      createFirmResponse.description = this.state.companyDescription.trim();
      this.props.addFirm(createFirmResponse);
      const newFirmId = createFirmResponse.firmId;
      const contactIds = [];
      const vFirmInfo = {
        firmType: firmApiStruct.firmType,
        firmId: newFirmId,
        name: firmApiStruct.name,
        contacts: contactIds,
      };
      this.props.addVisibleFirm(vFirmInfo);
      this.props.addVisibleFirmWithContacts({ visibleFirmId: newFirmId, contactIdsArray: contactIds });
      this.addFirm2FirmVisibility(newFirmId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildFirmApiStruct() {
    /* expected structure:
      public int FirmId { get; set; }
      public string Name { get; set; }
      public string FirmType { get; set; }
      public string Description { get; set; }
      public List<FirmContactApiStruct> Contacts { get; set; } = new List<FirmContactApiStruct>();
      public List<FacilityApiStruct> Facilities { get; set; } = new List<FacilityApiStruct>();
      public List<int> ReferralFromIds { get; set; } = new List<int>();
      public List<ReferralApiStruct> ReferralsTo { get; set; } = new List<ReferralApiStruct>();
      public List<WebLinkApiStruct> WebLinks { get; set; }
  */
    const isForPractitioner = this.state.firmType === 'Private Practitioner';
    const trimmedName = this.state.companyName.trim();
    const companyName = isForPractitioner ? trimmedName + "'s Practice" : trimmedName;
    const facilityApiStruct = this.buildFacilityApiStruct(companyName);
    const webLinks = this.state.webUrl ? [{ linkType: 'Website', url: this.state.webUrl }] : [];
    return {
      firmId: 0,
      name: companyName,
      firmType: this.state.firmType,
      description: this.state.companyDescription.trim(),
      temperature: this.state.companyTemperature,
      contacts: [],
      facilities: [facilityApiStruct],
      webLinks,
    };
  }

  buildFacilityApiStruct(companyName) {
    /*
    public int FacilityId { get; set; }
    public string Name { get; set; }
    public int FirmId { get; set; }
    public string PictureUrl { get; set; }
    public AddressApiStruct Address { get; set; }
    public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
  */
    const phoneNumbers = [];
    const addressApiStruct = this.buildAddressApiStruct();
    return {
      facilityId: 0,
      name: companyName,
      firmId: 0,
      facilityAddress: addressApiStruct,
      phoneNumbers: phoneNumbers,
    };
  }

  buildAddressApiStruct() {
    /*
    public string Roles { get; set; }
    public string IsIntlStr { get; set; }
    public string Line1 { get; set; }
    public string Line2 { get; set; }
    public string City { get; set; }
    public string State { get; set; }
    public string Zip { get; set; }
    public string IntlAddr { get; set; }
  */
    const { street, city, state, zip } = this.state;
    if (street.trim()) {
      return {
        roles: 'Business',
        isIntlStr: 'false',
        line1: street.trim(),
        line2: '',
        city: city.trim(),
        state,
        zip,
      };
    }
  }

  addFirm2FirmVisibility(newFirmId) {
    const viewingFirmId = this.props.viewingFirmId;
    const uri = `/api/firm/${viewingFirmId}/visibleFirm`;
    const f2fVApiStruct = this.buildFirm2FirmVisibilityApiStruct(viewingFirmId, newFirmId);
    const request = createStdRequest(uri, 'POST', f2fVApiStruct);
    const actionOnGoodResponse = (json) => {
      this.addFirmContact(viewingFirmId, newFirmId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildFirm2FirmVisibilityApiStruct(viewingFirmId, visibleFirmId) {
    /*
    public int Firm2FirmVisibilityId { get; set; }
    public int ViewingFirmId { get; set; }
    public int VisibleFirmId { get; set; }
    public string FirmType { get; set; }
    public string Description { get; set; }
    public string Temperature { get; set; }
  */
    return {
      firm2FirmVisibilityId: 0,
      viewingFirmId: viewingFirmId,
      visibleFirmId: visibleFirmId,
      firmType: this.state.firmType,
      description: this.state.companyDescription.trim(),
      temperature: this.state.companyTemperature,
    };
  }

  addFirmContact(viewingFirmId, visibleFirmId) {
    const uri = `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact`;
    const contactApiStruct = this.buildContactApiStruct(viewingFirmId, visibleFirmId);
    const request = createStdRequest(uri, 'POST', contactApiStruct);
    const actionOnGoodResponse = (json) => {
      const contactApiResponse = json;
      const reduxPersonStruct = ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(contactApiResponse);
      this.props.addPerson(reduxPersonStruct);
      const contactId = reduxPersonStruct.personId;
      const contactIdsArray = [contactId];
      this.props.addVisibleFirmWithContacts({ visibleFirmId, contactIdsArray });
      this.updateFirmContactIds(visibleFirmId, contactIdsArray);
      const contactName = reduxPersonStruct.preferredFullName;
      const contactIdAndName = { id: contactId, name: contactName };
      this.props.addFirmContact({ referralFirmId: visibleFirmId, personIdAndName: contactIdAndName });
      this.props.onSuccess(visibleFirmId);
      this.setState(this.cleanState);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildContactApiStruct(viewingFirmId, visibleFirmId) {
    /*
    public int PersonId { get; set; }
    public string Email { get; set; }
    public string FirstName { get; set; }
    public string MiddleNames { get; set; }
    public string LastName { get; set; }
    public string PreferredName { get; set; }
    public string Sex { get; set; }
    public string BirthDate { get; set; }
    public string LatestContactDate { get; set; }
    public OfficialIdApiStruct OfficialId { get; set; }
    public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
    public List<AddressApiStruct> Addresses { get; set; } = new List<AddressApiStruct>();
    
    public int FirmId { get; set; }
    public int ViewingFirmId { get; set; }
    public string JobTitle { get; set; }
    public string Description { get; set; }
  */
    const isForPractitioner = this.state.firmType === 'Private Practitioner';
    const name = isForPractitioner ? this.state.companyName : this.state.contactName;
    const trimmedName = name.trim();
    const firstSpaceLoc = trimmedName.indexOf(' ');
    const lastSpaceLoc = trimmedName.lastIndexOf(' ');
    const firstName = trimmedName.substring(0, firstSpaceLoc);
    const lastName = lastSpaceLoc >= firstSpaceLoc ? trimmedName.substring(lastSpaceLoc + 1) : '';
    const middleNames =
      lastSpaceLoc > firstSpaceLoc ? trimmedName.substring(firstSpaceLoc + 1, lastSpaceLoc).trim() : '';
    const phoneNumbers = [];
    if (this.state.contactPhone1Number) {
      const phoneApiStruct = {
        roles: this.state.contactPhone1Roles,
        countryCode: 1,
        number: this.state.contactPhone1Number,
      };
      phoneNumbers.push(phoneApiStruct);
    }
    if (this.state.contactPhone2Number) {
      const phoneApiStruct = {
        roles: this.state.contactPhone2Roles,
        countryCode: 1,
        number: this.state.contactPhone2Number,
      };
      phoneNumbers.push(phoneApiStruct);
    }
    return {
      personId: 0,
      email: this.state.contactEmail,
      firstName: firstName,
      middleNames: middleNames,
      lastName: lastName,
      preferredName: firstName,
      sex: 'Unknown',
      birthDate: null,
      latestContactDate: '',
      phoneNumbers: phoneNumbers,
      addresses: [],
      firmId: visibleFirmId,
      viewingFirmId: viewingFirmId,
      jobTitle: isForPractitioner ? '' : this.state.contactJobTitle.trim(),
      description: isForPractitioner ? '' : this.state.contactDescription.trim(),
      ownerId: this.state.contactOwnerId,
      temperature: this.state.contactTemperature,
    };
  }

  updateFirmContactIds(visibleFirmId, contactIdsArray) {
    const firm = DA.getFirmById(visibleFirmId);
    const modifiedFirm = { ...firm, contactIds: contactIdsArray };
    this.props.updateFirm(modifiedFirm);
  }

  validateForm() {
    let validation = this.validateCompanyName();
    if (!validation.valid) {
      return validation;
    }

    if (this.validationPolicy.requireFirmType) {
      validation = this.validateFirmType();
      if (!validation.valid) {
        return validation;
      }
    }

    validation = this.validateAddress();
    if (!validation.valid) {
      return validation;
    }

    if (this.validationPolicy.requireContact) {
      validation = this.validateContact();
      if (!validation.valid) {
        return validation;
      }
    }
    return { valid: true };
  }

  validateFirmType() {
    if (this.state.firmType) {
      return { valid: true };
    } else {
      return { valid: false, errorMessage: 'You must select a Firm Type' };
    }
  }

  validateCompanyName() {
    if (this.state.companyName.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Company Name' };
    } else if (this.state.companyName.trim().length < 6) {
      return { valid: false, errorMessage: 'You must supply a better Company Name' };
    } else {
      return { valid: true };
    }
  }

  validateAddress() {
    let { street, city, state, zip } = this.state;
    if (state === 'select') {
      state = '';
    }
    const { requireAddress, forbidPartialAddress, allowZipFreeAddress } = this.validationPolicy;
    return validateAddress(street, '', city, state, zip, requireAddress, forbidPartialAddress, allowZipFreeAddress);
  }

  validateContact() {
    let validation = this.validateContactName();
    if (!validation.valid) {
      return validation;
    }

    if (this.validationPolicy.requireJobTitle) {
      if (this.state.firmType !== 'Private Practitioner' && !this.state.contactJobTitle.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a Job Title for the Contact',
        };
      }
    }

    if (this.validationPolicy.requireContactDescription) {
      if (!this.state.contactDescription.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a Description for the Contact',
        };
      }
    }

    if (this.validationPolicy.requireContactPhone) {
      validation = this.validateContactPhones();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactEmail) {
      validation = this.validateContactEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireAtLeaseOneOfContactPhoneOrEmail) {
      validation = this.validateContactPhoneOrEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    validation = this.validateContactPhonesAndRolesConsistency();
    if (!validation.valid) {
      return validation;
    }

    return { valid: true };
  }

  validateContactName() {
    if (this.state.firmType !== 'Private Practitioner' && this.state.contactName.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Contact Person Name' };
    } else {
      return { valid: true };
    }
  }

  validateContactPhones() {
    // If we get here, there must be at least one phone number, so
    //  If there isn't one, report that problem,
    //  If there is, then make sure, for both phone numbers, that there's a role
    //    if and only if there's a number, and the number, if it exists, is valid.
    if (!this.state.contactPhone1Number) {
      return { valid: false, errorMessage: 'You must supply at least one phone number' };
    }

    let validation = this.validatePhoneNumberAndRoles(
      this.state.contactPhone1Number,
      this.state.contactPhone1Roles,
      'Phone 1'
    );
    if (!validation.valid) {
      return validation;
    }

    validation = this.validatePhoneNumberAndRoles(
      this.state.contactPhone2Number,
      this.state.contactPhone2Roles,
      'Phone 2'
    );
    if (!validation.valid) {
      return validation;
    }

    return { valid: true };
  }

  validateContactEmail() {
    if (!this.state.contactEmail) {
      return { valid: false, errorMessage: 'You must supply an email address for the contact' };
    }
    return validateEmail(this.state.contactEmail, 'Email does not appear to be in a valid format');
  }

  validatePhoneNumberAndRoles(number, roles, label) {
    if (number) {
      const validation = validatePhoneNumber(number, label);
      if (!validation.valid) {
        return validation;
      } else {
        return this.validateContactPhoneAndRolesConsistency(number, roles, label);
      }
    } else {
      return this.validateContactPhoneAndRolesConsistency(number, roles, label);
    }
  }

  validateContactPhoneOrEmail() {
    if (this.state.contactEmail || this.state.contactPhone1Number) {
      if (this.state.contactEmail) {
        return validateEmail(this.state.contactEmail, "The supplied Contact Email doesn't appear to be valid");
      } else {
        return validatePhoneNumber(this.state.contactPhone1Number, 'Phone 1');
      }
    } else {
      return {
        valid: false,
        errorMessage: 'You must supply (at least) either a phone number or an email address for the Contact',
      };
    }
  }

  validateContactPhonesAndRolesConsistency() {
    const validation = this.validateContactPhoneAndRolesConsistency(
      this.state.contactPhone1Number,
      this.state.contactPhone1Roles,
      'Phone 1'
    );
    if (!validation.valid) {
      return validation;
    }
    return this.validateContactPhoneAndRolesConsistency(
      this.state.contactPhone2Number,
      this.state.contactPhone2Roles,
      'Phone 2'
    );
  }

  validateContactPhoneAndRolesConsistency(number, roles, label) {
    if (number) {
      if (roles) {
        return { valid: true };
      } else {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have supplied a number, you must also check a role',
        };
      }
    } else {
      if (roles) {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have  checked a role, you must also supply a number',
        };
      } else {
        return { valid: true };
      }
    }
  }

  cancelForm() {
    this.props.onClose({ action: 'canceled' });
  }

  handleContactPhone1RolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhone1Roles: newPhoneRoles });
  }

  handleContactPhone1NumberChange(newNumber) {
    this.setState({ contactPhone1Number: newNumber });
  }

  handleContactPhone2RolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhone2Roles: newPhoneRoles });
  }

  handleContactPhone2NumberChange(newNumber) {
    this.setState({ contactPhone2Number: newNumber });
  }

  handleBusinessTypeChange(newValue) {
    this.setState({ firmType: newValue });
  }

  render() {
    if (this.state.errorMessage) {
      return <h3>{this.state.errorMessage}</h3>;
    }

    const { classes, show, fixedOrgType } = this.props;
    const isForPractitioner = this.state.firmType === 'Private Practitioner';
    const firmNameInputLabel = isForPractitioner ? 'Practitioner Name' : 'Company Name';
    const contactSectionLabel = isForPractitioner ? 'Contact Info' : 'Contact';
    const shouldShowContactNameInput = !isForPractitioner;
    const possibleOwners = DA.getAllFirmUsers();
    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <Modal
          classes={{ modal: classes.newReferrerOrgModal }}
          open={show}
          title="Register a Referring Organization"
          okButtonLabel="Register"
          cancelButtonLabel="Cancel"
          onOk={() => this.validateAndSubmit()}
          onCancel={() => this.cancelForm()}
        >
          <Grid container>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <BusinessTypeWidget
                    visibleFirmId={0}
                    filterOutPrivatePractitioners={true}
                    fixedType={fixedOrgType}
                    onChange={(newFirmType) => this.handleBusinessTypeChange(newFirmType)}
                  />
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label={firmNameInputLabel}
                      id="companyName"
                      required
                      fullWidth
                      value={this.state.companyName}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label={isForPractitioner ? 'Description' : 'Company Description'}
                      id="companyDescription"
                      fullWidth
                      value={this.state.companyDescription}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Street"
                      id="street"
                      fullWidth
                      value={this.state.street}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="City"
                      id="city"
                      fullWidth
                      value={this.state.city}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.stateAndZipDiv}>
                    <div className={classes.stateDiv}>
                      <StateSelector
                        id="company-state"
                        value={this.state.state}
                        onChange={(newValue) => this.handleCompanyStateChange(newValue)}
                      />
                    </div>
                    <div className={classes.zipDiv}>
                      <CrmTextField
                        label="Zip Code"
                        id="zip"
                        fullWidth
                        value={this.state.zip}
                        onChange={(event) => this.setState({ zip: event.target.value.trim() })}
                      />
                    </div>
                  </div>
                  {!isForPractitioner && (
                    <div style={{ marginTop: '16px' }}>
                      <WebLinkEditor
                        label="Website"
                        linkBoxWidth="220px"
                        initialValue={this.state.webUrl}
                        clickawayShouleSave={false}
                        onDelete={() => {}}
                        onNewValue={(newText) => {
                          this.setState({ webUrl: newText });
                        }}
                      />
                    </div>
                  )}
                  <div>
                    <KSelect
                      label="Company Temperature"
                      fullWidth={true}
                      liveOptions={this.possibleCompanyTemperatures}
                      liveValues={this.possibleCompanyTemperatures}
                      value={this.state.companyTemperature}
                      onChange={(event) => this.setState({ companyTemperature: event.target.value })}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <h5
                    className={
                      isForPractitioner ? classes.contactSectionLabelForPractitioner : classes.contactSectionLabel
                    }
                  >
                    {contactSectionLabel}
                  </h5>
                  <div className={classes.contactSectionFields}>
                    {shouldShowContactNameInput && (
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          label="Name"
                          id="contactName"
                          fullWidth
                          value={this.state.contactName}
                          onChange={(event) => this.handleTextChange(event)}
                        />
                      </div>
                    )}
                    {!isForPractitioner && (
                      <div>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            label="Job Title"
                            id="contactJobTitle"
                            fullWidth
                            value={this.state.contactJobTitle}
                            onChange={(event) => this.handleTextChange(event)}
                          />
                        </div>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            label="Contact Description"
                            id="contactDescription"
                            fullWidth
                            value={this.state.contactDescription}
                            onChange={(event) => this.handleTextChange(event)}
                          />
                        </div>
                      </div>
                    )}
                    <div style={{ marginTop: '14px' }}>
                      <PhoneInput
                        label="Phone 1"
                        possibleRoles={this.possibleContactPhoneRoles}
                        actualRoles={this.state.contactPhone1Roles}
                        number={this.state.contactPhone1Number}
                        onRolesChange={(rolesObj) => this.handleContactPhone1RolesChange(rolesObj)}
                        onNumberChange={(newNumber) => this.handleContactPhone1NumberChange(newNumber)}
                      />
                      <div className={classes.phone2Div}>
                        <PhoneInput
                          label="Phone 2"
                          possibleRoles={this.possibleContactPhoneRoles}
                          actualRoles={this.state.contactPhone2Roles}
                          number={this.state.contactPhone2Number}
                          onRolesChange={(rolesObj) => this.handleContactPhone2RolesChange(rolesObj)}
                          onNumberChange={(newNumber) => this.handleContactPhone2NumberChange(newNumber)}
                        />
                      </div>
                    </div>
                    <div
                      className={isForPractitioner ? classes.emailAddressDivForPractitioner : classes.textFieldHolder}
                    >
                      <CrmTextField
                        label="Email Address"
                        id="contactEmail"
                        fullWidth
                        value={this.state.contactEmail}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div>
                      <KSelect
                        label="Contact Temperature"
                        fullWidth={true}
                        liveOptions={this.possibleContactTemperatures}
                        liveValues={this.possibleContactTemperatures}
                        value={this.state.contactTemperature}
                        onChange={(event) => this.setState({ contactTemperature: event.target.value })}
                      />
                    </div>
                    <div>
                      <KSelect
                        label="Owner"
                        fullWidth={true}
                        liveOptions={possibleOwners.map((c) => c.name)}
                        liveValues={possibleOwners.map((c) => c.personId)}
                        value={this.state.contactOwnerId}
                        onChange={(event) => this.setState({ contactOwnerId: event.target.value })}
                      />
                    </div>
                    {isForPractitioner && (
                      <div style={{ marginTop: '19px' }}>
                        <WebLinkEditor
                          label="Website"
                          linkBoxWidth="200px"
                          initialValue={this.state.webUrl}
                          clickawayShouleSave={false}
                          onDelete={() => {}}
                          onNewValue={(newText) => {
                            this.setState({ webUrl: newText });
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
});

const actionProducers = {
  addFirm,
  updateFirm,
  addPerson,
  addVisibleFirm,
  addFirmContact,
  addVisibleFirmWithContacts,
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(newReferrerOrgModalStyle)(NewReferrerOrgModal));
